import { unifyNames, type DocNodeJSON, extractDocNames, type NoteRecord } from '@lapo/core';

/**
 * Client side of the `[[`/`#` entity-index autocomplete (IMPL-07). Candidates come from two places,
 * merged into one unified namespace (wikilinks and tags are the same entity, so one list serves both
 * triggers):
 *
 *  1. the server entity index — the global "what exists" set, via `GET /entities/search` (the server
 *     half of IMPL-07), available when `VITE_API_URL` + a token are configured;
 *  2. a local doc-name index — names already referenced in the notes open on this client, so
 *     offline / local-first use still autocompletes against what you've written.
 *
 * Remote failures degrade silently to the local index; the picker always also offers "create new".
 */
const API_URL = import.meta.env.VITE_API_URL;

interface EntityHit {
  name: string;
  /** entity kind — lets the picker route initiatives to `+` and keep them out of `[[ ]]`/`#`. */
  type?: string;
}

/** Which slice of the entity index a trigger wants. */
export type EntityFilter = 'initiative' | 'exclude-initiative';

/**
 * Small in-memory cache of entity-index responses keyed by `limit:query`, so re-typing a prefix (or
 * backspacing back onto one) is instant and doesn't re-hit the network. The picker is local-first
 * anyway; this just trims redundant augment requests. Bounded so it can't grow without limit.
 */
const remoteCache = new Map<string, string[]>();
const REMOTE_CACHE_MAX = 200;

/** Query the server entity index. Returns [] when unconfigured or on any error (offline-friendly). */
export async function searchEntityIndex(
  query: string,
  token: string | null,
  limit = 10,
  signal?: AbortSignal,
  filter?: EntityFilter,
): Promise<string[]> {
  if (!API_URL || !token) return [];
  const cacheKey = `${limit}:${filter ?? 'all'}:${query.trim().toLowerCase()}`;
  const cached = remoteCache.get(cacheKey);
  if (cached) return cached;
  try {
    // Ask for more than `limit` so post-filtering by type still fills the picker.
    const qs = new URLSearchParams({ q: query, limit: String(filter ? limit * 3 : limit) });
    const res = await fetch(`${API_URL}/entities/search?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { entities?: EntityHit[] };
    const keep = (e: EntityHit): boolean =>
      filter === 'initiative'
        ? e.type === 'initiative'
        : filter === 'exclude-initiative'
          ? e.type !== 'initiative'
          : true;
    const names = (body.entities ?? [])
      .filter(keep)
      .map((e) => e.name)
      .filter(Boolean)
      .slice(0, limit);
    if (remoteCache.size >= REMOTE_CACHE_MAX) remoteCache.clear();
    remoteCache.set(cacheKey, names);
    return names;
  } catch {
    return []; // network error / aborted / not configured → local index only
  }
}

/**
 * Fetch the note registry (architecture §4) over `GET /notes` so this device can enumerate the
 * user's pages — journals and documents alike — without holding every Y.Doc. Returns [] when the
 * API/token is unconfigured or on any error, so the app stays usable offline (it falls back to the
 * locally-derived day list). userId is taken server-side from the token, never sent by the client.
 */
export async function fetchNotes(token: string | null, signal?: AbortSignal): Promise<NoteRecord[]> {
  // Needs the caller's LIVE auth token — there is no static env token in the deployed/lapo-dev flow.
  // API_URL is same-origin (`/api`), so build the path by concatenation, not `new URL()` (which throws
  // on a relative base).
  if (!API_URL || !token) return [];
  try {
    const res = await fetch(`${API_URL}/notes`, {
      headers: { authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { notes?: NoteRecord[] };
    return body.notes ?? [];
  } catch {
    return []; // offline / not configured → local day list only
  }
}

/**
 * A local, in-memory index of entity names this client has seen, so autocomplete works offline and
 * stays warm between keystrokes. Pages register their current doc names; lookups merge that with any
 * seed names (e.g. favorited page titles).
 */
export class LocalNameIndex {
  private byNote = new Map<string, string[]>();
  constructor(private readonly seed: readonly string[] = []) {}

  /** Record the names a note currently references (replaces that note's previous contribution). */
  updateFromDoc(noteId: string, doc: DocNodeJSON | null | undefined): void {
    this.byNote.set(noteId, extractDocNames(doc));
  }

  forget(noteId: string): void {
    this.byNote.delete(noteId);
  }

  /** All known local names, de-duplicated, seeds first so their casing wins. */
  all(): string[] {
    return unifyNames(this.seed, ...this.byNote.values());
  }
}
